import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Activity } from 'lucide-react';
import { BarChart, LineChart } from './charts';
import { heroDashboardMetrics, heroChart, heroFloatingLabels } from '../lib/liveData';

export default function HeroDashboard() {
  return (
    <section className="relative pt-36 md:pt-44 pb-16 overflow-hidden">
      <div className="absolute inset-0" aria-hidden>
        <div className="orb w-[440px] h-[440px] bg-[#2563eb]/25 -top-24 right-0 float-slow" />
        <div className="orb w-[360px] h-[360px] bg-[#4f46e5]/20 top-40 -left-20" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        {/* Copy */}
        <div>
          <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-medium text-slate-700 mb-6">
            <span className="relative w-2 h-2 rounded-full bg-[#2563eb] ping-slow" /> Acquire. Optimize. Scale.
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-slate-900">
            Performance Marketing <span className="text-gradient">Built to Scale.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-xl">
            We help brands and apps acquire high-quality users, drive measurable growth and scale performance across global markets.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#2563eb] to-[#1d4ed8] px-7 py-3.5 font-semibold text-white hover:opacity-90 transition-opacity blue-glow">
              Let's Grow Together <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/solutions" className="inline-flex items-center gap-2 rounded-full glass px-7 py-3.5 font-semibold text-slate-900 hover:border-[#2563eb]/50 transition-colors">
              Explore Our Solutions
            </Link>
          </div>
        </div>

        {/* Dashboard */}
        <div className="relative">
          {/* floating labels */}
          <span className="absolute -top-4 -left-3 z-20 hidden sm:inline-flex items-center gap-1.5 rounded-full glass-strong px-3 py-1.5 text-xs font-medium text-slate-700 float-slow">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2563eb]" /> {heroFloatingLabels[0]}
          </span>
          <span className="absolute top-1/3 -right-3 z-20 hidden sm:inline-flex items-center gap-1.5 rounded-full glass-strong px-3 py-1.5 text-xs font-medium text-slate-700 float-x">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4f46e5]" /> {heroFloatingLabels[1]}
          </span>
          <span className="absolute -bottom-3 left-6 z-20 hidden sm:inline-flex items-center gap-1.5 rounded-full glass-strong px-3 py-1.5 text-xs font-medium text-slate-700 float-slow">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2563eb]" /> {heroFloatingLabels[3]}
          </span>

          <div className="relative glass-strong rounded-3xl p-5 md:p-6 blue-glow">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2563eb] to-[#1d4ed8] grid place-items-center"><Activity size={16} className="text-white" /></span>
                <div>
                  <div className="text-sm font-semibold text-slate-900 leading-none">Performance Intelligence</div>
                  <div className="text-[10px] text-slate-500 mt-1">Live · updated daily · sample data</div>
                </div>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/12 px-2.5 py-1 text-[11px] font-semibold text-emerald-600"><TrendingUp size={12} /> +18.7%</span>
            </div>

            <div className="grid grid-cols-3 gap-2.5">
              {heroDashboardMetrics.map((m) => (
                <div key={m.label} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] text-slate-500">{m.label}</div>
                  <div className="font-display text-lg font-bold text-slate-900 leading-tight">{m.value}</div>
                  <div className="text-[10px] text-emerald-600">{m.trend}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] text-slate-500 mb-1.5">Installs</div>
                <BarChart data={heroChart} color="#2563eb" height={70} />
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] text-slate-500 mb-1.5">Revenue</div>
                <LineChart data={heroChart} color="#4f46e5" height={70} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
